import type { MonogramPattern, MonogramPageJson, MonogramPatternContext, MonogramJson } from './websiteTemplates.types';

const pad = (value: number): string => String(value).padStart(2, '0');
const monthDay = (date: Date): string => `${pad(date.getUTCMonth() + 1)}.${pad(date.getUTCDate())}`;
const fullYear = (date: Date): string => String(date.getUTCFullYear());
// Stacked variants render the date across two lines (templates like Rose Marble author it that way).
const monthDayStacked = (date: Date): string => `${pad(date.getUTCMonth() + 1)}\n${pad(date.getUTCDate())}`;
// Splits the 4-digit year into two stacked lines, e.g. 2027 -> '20'/'27'.
const yearStacked = (date: Date): string => {
  const year = fullYear(date);
  return `${year.slice(0, 2)}\n${year.slice(2)}`;
};

// Preserves the template glyph's case (some monograms are authored lowercase, e.g. h, g, k, b).
const matchCase = (template: string, value: string): string => (template === template.toLowerCase() ? value.toLowerCase() : value.toUpperCase());

const initialOf = (name: string | null | undefined, template: string): string | null => {
  const letter = name?.trim()[0];
  return letter ? matchCase(template, letter.toUpperCase()) : null;
};

/**
 * One builder per pattern: given the personalization context and the layer's current (placeholder)
 * text, returns the replacement string — or null to leave the layer untouched (a value the event
 * doesn't supply, e.g. a missing name or date). Adding a new shape = adding a pattern + a builder.
 */
export const MONOGRAM_PATTERN_BUILDERS: Record<MonogramPattern, (ctx: MonogramPatternContext, current: string) => string | null> = {
  coupleOneInitial: (ctx, current) => initialOf(ctx.couple[0], current),
  coupleTwoInitial: (ctx, current) => initialOf(ctx.couple[1], current),
  coupleOneName: ctx => ctx.couple[0]?.trim() || null,
  coupleTwoName: ctx => ctx.couple[1]?.trim() || null,
  'coupleOne&coupleTwo': ctx => {
    const one = ctx.couple[0]?.trim();
    const two = ctx.couple[1]?.trim();
    return one && two ? `${one} & ${two}` : null;
  },
  'month.day': ctx => (ctx.date ? monthDay(ctx.date) : null),
  year: ctx => (ctx.date ? fullYear(ctx.date) : null),
  'month.day.year': ctx => (ctx.date ? `${monthDay(ctx.date)}.${fullYear(ctx.date)}` : null),
  monthDayStacked: ctx => (ctx.date ? monthDayStacked(ctx.date) : null),
  yearStacked: ctx => (ctx.date ? yearStacked(ctx.date) : null),
  yearFirstHalf: ctx => (ctx.date ? fullYear(ctx.date).slice(0, 2) : null),
  yearSecondHalf: ctx => (ctx.date ? fullYear(ctx.date).slice(2) : null),
};

/**
 * Resolves every patterned editable text layer of a monogram page through its builder. Layers with
 * no `pattern`, non-text layers, and builders that return null are left unchanged. Pure — returns a
 * new page; the input is not mutated.
 */
export const applyMonogramPatterns = (page: MonogramPageJson, ctx: MonogramPatternContext): MonogramPageJson => ({
  ...page,
  layers: page.layers.map(layer => {
    if (layer.type !== 'text' || !layer.editable || !layer.pattern) return layer;
    const content = MONOGRAM_PATTERN_BUILDERS[layer.pattern](ctx, layer.textData.content);
    if (content === null) return layer;
    return { ...layer, textData: { ...layer.textData, content } };
  }),
});

/**
 * Builds the personalization context from the couple's names + event date. Initials only fill when
 * BOTH names are present — a single real initial beside a template placeholder produces a mixed
 * monogram. The event date is independent. Shared by every personalization entry point.
 */
export const buildMonogramPatternContext = (
  ownerFirstName: string | null | undefined,
  fianceeFirstName: string | null | undefined,
  eventDateMs: number | null | undefined,
): MonogramPatternContext => {
  const bothNames = !!(ownerFirstName?.trim() && fianceeFirstName?.trim());
  return {
    couple: bothNames ? [ownerFirstName, fianceeFirstName] : [],
    date: eventDateMs ? new Date(eventDateMs) : null,
  };
};

/**
 * Personalizes a typed inline-template `MonogramJson` via its layer patterns. Use this for the
 * client-side template data (gallery tiles); `personalizeMonogramContent` handles the untyped
 * GraphQL-fetched container shape.
 */
export const personalizeMonogramJson = (
  json: MonogramJson,
  ownerFirstName: string | null | undefined,
  fianceeFirstName: string | null | undefined,
  eventDateMs: number | null | undefined,
): MonogramJson => ({
  ...json,
  card: { ...json.card, front: applyMonogramPatterns(json.card.front, buildMonogramPatternContext(ownerFirstName, fianceeFirstName, eventDateMs)) },
});

/**
 * Removes the authoring-only `pattern` key from a monogram content object's layers before it is
 * persisted. `pattern` lives on the inline template data for client-side personalization; it must
 * never reach the backend json container (it isn't part of the monogram container schema). Operates
 * on the untyped persisted shape; returns the input unchanged when there are no layers to clean.
 */
export const stripMonogramPatternFields = (content: Record<string, unknown>): Record<string, unknown> => {
  const card = content?.card as Record<string, unknown> | undefined;
  const front = card?.front as Record<string, unknown> | undefined;
  const layers = front?.layers as ReadonlyArray<Record<string, unknown>> | undefined;
  if (!layers) return content;

  return {
    ...content,
    card: {
      ...card,
      front: {
        ...front,
        layers: layers.map(layer => {
          if (!layer || !('pattern' in layer)) return layer;
          const next = { ...layer };
          delete next.pattern;
          return next;
        }),
      },
    },
  };
};
